import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getCsrfToken } from '../../../utils/csrf'
import {
  ApiAssetTotal,
  ApiIndividualAsset,
  ApiResponse,
  AssetTotal,
  IndividualAsset
} from '../../models/ModelInvestments'

export const useStocksDashboard = () => {
  const [assetTotals, setAssetTotals] = useState<AssetTotal[]>([])
  const [individualAssets, setIndividualAssets] = useState<IndividualAsset[]>([])
  const [showUSD, setShowUSD] = useState(false)
  const [exchangeRate, setExchangeRate] = useState<number>(5)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isRecalculating, setIsRecalculating] = useState(false)
  
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const currencyFilter = queryParams.get('currency')?.toUpperCase()

  const fetchStocksDashboardData = useCallback(async () => {
    try {
      if (isInitialLoad) {
        setIsLoading(true)
      }

      const csrfToken = await getCsrfToken()
      const url = currencyFilter 
        ? `/api/investments/dashboard/stocks/?currency=${currencyFilter}`
        : '/api/investments/dashboard/stocks/'

      const response = await fetch(url, {
        method: 'GET', 
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data: ApiResponse = await response.json()

      if (data.success) {
        
        const mappedAssets = data.data.asset_totals.map((asset: ApiAssetTotal) => ({
          asset_type: asset.asset_type,
          currency: asset.currency,
          total_value: String(asset.total_invested_amount || '0'),
          total_current_value: String(asset.total_current_value || '0'),
          total_dividends: String(asset.total_dividends || '0'),
          total_invested_amount: String(asset.total_invested_amount || '0'),
          total_profit: String(asset.total_profit || '0'),
          total_return: String(asset.total_return || '0')
        }))

        const getLogoUrl = (ticker: string) => {
          if (ticker === 'ROXO34') {
            ticker = 'NU'
          }
          if (ticker === 'BERK34') {
            ticker = 'BRK-B'
          } 
          if (ticker === 'AMZO34') {
            ticker = 'AMZN'
          }
          if (ticker === 'APPL34') {
            ticker = 'AAPL'
          }
          if (ticker === 'GOOG34') {
            ticker = 'GOOGL'
          }
          if (ticker === 'NVDC34') {
            ticker = 'NVDA'
          }
          if (ticker === 'COCA34') {
            ticker = 'KO'
          }
          if (ticker === 'P2LT34') {
            ticker = 'PLTR'
          }

          const specialCases: { [key: string]: string } = {
            'META': 'https://companieslogo.com/img/orig/META-4767da84.png',
            'GOOGL': 'https://www.google.com/favicon.ico',
            'PLTR': 'https://companieslogo.com/img/orig/PLTR.D-618f91d3.png',
            'NU': 'https://companieslogo.com/img/orig/NU-8c6ed5e4.png',
            'BRK-B': 'https://companieslogo.com/img/orig/BRK-B-25f11589.png',

            'ITUB3': 'https://companieslogo.com/img/orig/ITUB-1ebef8c5.png',
            'KO': 'https://companieslogo.com/img/orig/2579.T-09bc4e0a.png',
            'VALE3': 'https://companieslogo.com/img/orig/VALE-9213b7ec.png',
            'ABEV3': 'https://companieslogo.com/img/orig/ABEV-c09eab6f.png',
            'BBAS3': 'https://companieslogo.com/img/orig/BBAS3.SA-3b4546c4.png',
            'FLRY3': 'https://cdn.cosmicjs.com/4ee53c30-7063-11e9-948f-7b6a64396c21-icon.png'
            // Add more special cases as needed
          };

          if (specialCases[ticker]) {
            return specialCases[ticker];
          }

          // Handle Brazilian BDRs (ending with 34)
          if (ticker.endsWith('34')) {
            const baseTickerName = ticker.replace('34', '');
            return `https://storage.googleapis.com/iex/api/logos/${baseTickerName}.png`;
          }

          // Fallback to IEX for US stocks
          return `https://storage.googleapis.com/iex/api/logos/${ticker}.png`;
        }

        const mappedIndividualAssets = data.data.individual_assets.map((asset: ApiIndividualAsset) => ({
          ticker: asset.ticker,
          currency: asset.currency,
          invested_amount: String(asset.invested_amount || '0'),
          current_amount: String(asset.current_amount || '0'),
          dividends: String(asset.dividends || '0'),
          profit: String(asset.profit || '0'),
          return: String(asset.return || '0'),
          logo_url: getLogoUrl(asset.ticker)
        }))
        
        setAssetTotals(mappedAssets)
        setIndividualAssets(mappedIndividualAssets)
        
        if (data.data.exchange_rate) {
          const rate = parseFloat(data.data.exchange_rate.toString())
          setExchangeRate(isNaN(rate) ? 5 : rate)
        }

        // Update recalculating status
        setIsRecalculating(!!data.data.is_recalculating)
      } else {
        console.error('Error fetching stock dashboard data:', data)
        setIsRecalculating(false)
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setIsRecalculating(false)
    } finally {
      if (isInitialLoad) {
        setIsLoading(false)
        setIsInitialLoad(false)
      }
    }
  }, [currencyFilter, isInitialLoad])

  useEffect(() => {
    fetchStocksDashboardData()
    
    const interval = setInterval(fetchStocksDashboardData, 30000)
    return () => clearInterval(interval)
  }, [fetchStocksDashboardData])

  return {
    assetTotals,
    individualAssets,
    showUSD,
    setShowUSD,
    exchangeRate,
    isLoading,
    isRecalculating,
    fetchStocksDashboardData
  }
}